import React, { useState, useEffect, useCallback } from 'react';
import { legalApi } from '../lib/api';
import { toast } from 'sonner';
import { Shield, Check } from 'lucide-react';
import { Button } from './ui/button';

const PolicyReconfirmation = ({ children }) => {
  const [pending, setPending] = useState([]);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [viewContent, setViewContent] = useState(null);

  const fetchPending = useCallback(async () => {
    try {
      const data = await legalApi.getPendingPolicies();
      setPending(data.pending || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const allChecked = pending.length > 0 && pending.every(p => checked[p.page_id]);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      const policies = pending.map(p => ({
        page_id: p.page_id,
        slug: p.slug,
        version: p.version,
      }));
      await legalApi.acceptPolicies({ policies, context: 'reconfirmation' });
      toast.success('Policies accepted');
      setPending([]);
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  if (loading) return children;
  if (pending.length === 0) return children;

  return (
    <>
      {/* Blocking overlay */}
      <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="policy-reconfirmation-overlay">
        <div className="w-full max-w-lg bg-[#0F0B1E] border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
          <div className="p-6 border-b border-white/10 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#8B5CF6]" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Updated Policies</h2>
              <p className="text-sm text-gray-400">Please review and accept the updated policies to continue.</p>
            </div>
          </div>

          <div className="p-6 space-y-4 max-h-[50vh] overflow-y-auto">
            {pending.map(policy => (
              <div key={policy.page_id} className="space-y-2">
                <div className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-[#030014]" data-testid={`reconfirm-policy-${policy.page_id}`}>
                  <input
                    type="checkbox"
                    checked={!!checked[policy.page_id]}
                    onChange={() => setChecked(p => ({ ...p, [policy.page_id]: !p[policy.page_id] }))}
                    className="accent-[#8B5CF6] mt-1 w-4 h-4 flex-shrink-0"
                    data-testid={`reconfirm-check-${policy.page_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{policy.title} <span className="text-xs text-gray-500 ml-1">v{policy.version}</span></p>
                    <button
                      onClick={() => setViewContent(viewContent === policy.page_id ? null : policy.page_id)}
                      className="text-xs text-[#8B5CF6] hover:text-[#A78BFA] mt-1" data-testid={`view-policy-${policy.page_id}`}
                    >
                      {viewContent === policy.page_id ? 'Hide content' : 'Read full policy'}
                    </button>
                  </div>
                </div>
                {viewContent === policy.page_id && policy.content && (
                  <div className="ml-7 p-3 rounded-lg bg-[#030014] border border-white/5 max-h-48 overflow-y-auto">
                    <div className="prose prose-invert prose-sm max-w-none text-gray-400 text-xs"
                      dangerouslySetInnerHTML={{ __html: policy.content }} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="p-6 border-t border-white/10">
            <Button
              onClick={handleAccept}
              disabled={!allChecked || submitting}
              className="w-full btn-primary h-11"
              data-testid="accept-policies-btn"
            >
              {submitting ? (
                <div className="spinner w-4 h-4" />
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Accept and Continue</>
              )}
            </Button>
            {!allChecked && (
              <p className="text-xs text-gray-500 text-center mt-2">Please check all policies to continue</p>
            )}
          </div>
        </div>
      </div>
      {children}
    </>
  );
};

export default PolicyReconfirmation;
